import * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export type Projection = 'perspective' | 'orthographic';

export type RigCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

export interface CameraPose {
  position: THREE.Vector3;
  target: THREE.Vector3;
}

export interface SnapToken {
  cancelled: boolean;
}

export type ViewPreset = 'perspective' | 'isometric' | 'front' | 'back' | 'right' | 'left' | 'top' | 'bottom';

// CAD convention is Z-up (Blender, Rhino, SolidWorks, AutoCAD, every slicer),
// three.js defaults to Y-up. Set before any Object3D is constructed so the rig
// cameras, the view cube's camera and OrbitControls' orbit axis all agree.
THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

const PERSPECTIVE_FOV = 45;
const INITIAL_NEAR = 0.1;
const INITIAL_FAR = 1000;
const TURN_RATE = 2 * Math.PI;
const SNAP_SETTLED_RADIANS = 0.0001;
const HOME_SECONDS = 0.5;
// camera.lookAt is degenerate along the up axis and rolls TOP/BOTTOM views
// arbitrarily, so those destinations land this far off the pole (as Fusion 360
// and Onshape do). Visually still an axial view.
const POLE_TILT_RADIANS = 0.001;
// 1 - cos(~0.8deg): accepts the pole-tilted views, rejects the first pixels of a drag.
export const AXIS_ALIGNMENT_TOLERANCE = 1e-4;
// Front-right-above 3/4 view in Z-up: +X right, -Y towards viewer, +Z up.
const FIT_DIRECTION = { x: 1, y: -1, z: 0.65 };
const FIT_DIRECTION_LENGTH = Math.hypot(FIT_DIRECTION.x, FIT_DIRECTION.y, FIT_DIRECTION.z);

const PRESET_DIRECTIONS: Record<ViewPreset, [number, number, number]> = {
  perspective: [FIT_DIRECTION.x, FIT_DIRECTION.y, FIT_DIRECTION.z],
  isometric: [1, -1, 1],
  front: [0, -1, 0],
  back: [0, 1, 0],
  right: [1, 0, 0],
  left: [-1, 0, 0],
  top: [0, 0, 1],
  bottom: [0, 0, -1],
};

/** Where the camera looks from, as a unit vector out from the target. */
export function presetDirection(preset: ViewPreset): THREE.Vector3 {
  return new THREE.Vector3(...PRESET_DIRECTIONS[preset]).normalize();
}

/** The projection a preset holds the view in; face views settle to orthographic on their own. */
export function presetProjection(preset: ViewPreset): Projection | null {
  if (preset === 'isometric') return 'orthographic';
  if (preset === 'perspective') return 'perspective';
  return null;
}
const FIT_PADDING = 1.1;
export const FALLBACK_MODEL_RADIUS = 1;
const MIN_DOLLY_RATIO = 0.001;
const MAX_DOLLY_MULTIPLIER = 50;
const NEAR_PLANE_RATIO = 0.01;
const FAR_PLANE_MARGIN = 4;
const MAX_DEPTH_RANGE = 1e5;

function halfFovTangent(camera: THREE.PerspectiveCamera): number {
  return Math.tan((camera.fov * Math.PI) / 360);
}

/**
 * One view held in two projections. Cube faces are shown orthographic, as in
 * CAD; everything else is perspective. Switching keeps the target plane the
 * same apparent size so nothing jumps on screen.
 */
export class CameraRig {
  readonly perspective: THREE.PerspectiveCamera;
  readonly orthographic: THREE.OrthographicCamera;
  private current: Projection = 'perspective';

  constructor(aspect: number) {
    this.perspective = new THREE.PerspectiveCamera(
      PERSPECTIVE_FOV,
      aspect,
      INITIAL_NEAR,
      INITIAL_FAR,
    );
    this.perspective.position.set(0, -5, 0);
    this.orthographic = new THREE.OrthographicCamera(-aspect, aspect, 1, -1, INITIAL_NEAR, INITIAL_FAR);
  }

  get projection(): Projection {
    return this.current;
  }

  get active(): RigCamera {
    return this.current === 'perspective' ? this.perspective : this.orthographic;
  }

  viewDirection(target: THREE.Vector3): THREE.Vector3 {
    return new THREE.Vector3().subVectors(this.active.position, target).normalize();
  }

  isAxisAlignedView(target: THREE.Vector3): boolean {
    const d = this.viewDirection(target);
    return Math.max(Math.abs(d.x), Math.abs(d.y), Math.abs(d.z)) >= 1 - AXIS_ALIGNMENT_TOLERANCE;
  }

  projectionForView(target: THREE.Vector3): Projection {
    return this.isAxisAlignedView(target) ? 'orthographic' : 'perspective';
  }

  setProjection(next: Projection, controls: OrbitControls): void {
    if (next === this.current) return;
    const from = this.active;
    switch (next) {
      case 'orthographic':
        this.enterOrthographic(from.position.distanceTo(controls.target), controls);
        break;
      case 'perspective':
        this.enterPerspective(controls.target);
        break;
      default: {
        const exhaustive: never = next;
        throw new Error(`Unknown projection: ${String(exhaustive)}`);
      }
    }
    this.current = next;
    const to = this.active;
    to.up.copy(from.up);
    to.quaternion.copy(from.quaternion);
    to.updateProjectionMatrix();
    controls.object = to;
    controls.update();
  }

  setAspect(aspect: number): void {
    this.perspective.aspect = aspect;
    this.perspective.updateProjectionMatrix();
    const ortho = this.orthographic;
    ortho.left = -ortho.top * aspect;
    ortho.right = ortho.top * aspect;
    ortho.updateProjectionMatrix();
  }

  // Per frame rather than bracketed once around the whole dolly range: that
  // range spans five orders of magnitude, more than the depth buffer can
  // resolve without z-fighting on the coplanar walls and slabs of a building.
  applyClipPlanes(controls: OrbitControls, sceneRadius: number): void {
    const camera = this.active;
    const distance = camera.position.distanceTo(controls.target);
    const depth = sceneRadius * FAR_PLANE_MARGIN;
    const far = distance + depth;
    // Negative near is valid for an orthographic camera; it keeps geometry
    // close to the eye point from being clipped after a deep zoom.
    const near =
      this.current === 'orthographic'
        ? distance - depth
        : Math.max(distance * NEAR_PLANE_RATIO, far / MAX_DEPTH_RANGE);
    if (camera.near === near && camera.far === far) return;
    camera.near = near;
    camera.far = far;
    camera.updateProjectionMatrix();
  }

  /** Shows exactly what another rig published as `toPose` and its projection. */
  applyView(pose: CameraPose, projection: Projection, controls: OrbitControls): void {
    // Momentum left from this viewer's own last drag would keep turning it off
    // the pose on every later update(); a single undamped update spends it.
    const damping = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    controls.enableDamping = damping;
    if (this.current === 'orthographic') {
      this.current = 'perspective';
      controls.object = this.perspective;
    }
    restoreCameraPose(this.perspective, controls, pose);
    this.setProjection(projection, controls);
  }

  /** Always perspective-equivalent, so a pose can be restored into a fresh rig. */
  toPose(target: THREE.Vector3): CameraPose {
    const position =
      this.current === 'perspective'
        ? this.perspective.position.clone()
        : target.clone().addScaledVector(this.viewDirection(target), this.orthographicDistance());
    return { position, target: target.clone() };
  }

  private enterOrthographic(distance: number, controls: OrbitControls): void {
    const halfHeight = distance * halfFovTangent(this.perspective);
    const halfWidth = halfHeight * this.perspective.aspect;
    const ortho = this.orthographic;
    ortho.top = halfHeight;
    ortho.bottom = -halfHeight;
    ortho.left = -halfWidth;
    ortho.right = halfWidth;
    ortho.zoom = 1;
    ortho.position.copy(this.perspective.position);
    // Ortho zoom scales the frame as 1/distance would, so the dolly limits map
    // onto zoom limits and the view can never leave the range the fit allowed.
    controls.minZoom = distance / controls.maxDistance;
    controls.maxZoom = distance / controls.minDistance;
  }

  private enterPerspective(target: THREE.Vector3): void {
    const direction = this.viewDirection(target);
    this.perspective.position.copy(target).addScaledVector(direction, this.orthographicDistance());
  }

  // Inverse of enterOrthographic: the ortho frustum is only ever sized against
  // this.perspective's fov, so the same fov turns a visible half-height back
  // into the perspective distance that shows it. A pose restored into a rig
  // with a different PERSPECTIVE_FOV would frame the model differently.
  private orthographicDistance(): number {
    const ortho = this.orthographic;
    return ortho.top / ortho.zoom / halfFovTangent(this.perspective);
  }
}

// A container that is collapsed or not yet laid out measures 0, and 0/0 is NaN:
// left alone it reaches the projection matrix and nothing draws at all.
export function aspectFor(width: number, height: number): number {
  const aspect = width / height;
  return Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
}

// Measured about the model centre, where the camera target — and so the far
// plane — is anchored. The grid is drawn around the target, so only its drop
// below the model counts, not the model's distance from the world origin.
export function sceneRadiusFor(box: THREE.Box3, gridSize: number, gridZ: number): number {
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const modelRadius = Math.hypot(size.x, size.y, size.z) / 2;
  const gridRadius = Math.abs(center.z - gridZ) + (gridSize / 2) * Math.SQRT2;
  const radius = Math.max(modelRadius, gridRadius);
  // An empty Box3 sizes to -Infinity and centres on NaN, either of which would
  // put the far plane at a non-finite distance.
  return Number.isFinite(radius) ? radius : FALLBACK_MODEL_RADIUS;
}

export function fitCameraToObject(
  camera: THREE.PerspectiveCamera,
  box: THREE.Box3,
  controls: OrbitControls,
): void {
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  if (!Number.isFinite(center.x + center.y + center.z)) center.set(0, 0, 0);

  const boundingRadius = Math.hypot(size.x, size.y, size.z) / 2;
  const radius =
    Number.isFinite(boundingRadius) && boundingRadius > 0 ? boundingRadius : FALLBACK_MODEL_RADIUS;

  // Frame the bounding sphere against whichever frustum half-angle is the
  // tighter of the two, so a wide model in a narrow panel is not cropped.
  const halfAngle = Math.atan(halfFovTangent(camera) * Math.min(1, camera.aspect));
  const distance = (radius / Math.sin(halfAngle)) * FIT_PADDING;

  // OrbitControls.update() clamps the orbit radius to these, so they must be
  // model-relative and set before that call or the fit itself gets clamped.
  controls.minDistance = radius * MIN_DOLLY_RATIO;
  controls.maxDistance = distance * MAX_DOLLY_MULTIPLIER;

  const step = distance / FIT_DIRECTION_LENGTH;
  camera.position.set(
    center.x + FIT_DIRECTION.x * step,
    center.y + FIT_DIRECTION.y * step,
    center.z + FIT_DIRECTION.z * step,
  );
  controls.target.copy(center);
  controls.update();
}

export function restoreCameraPose(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  pose: CameraPose,
): void {
  camera.position.copy(pose.position);
  controls.target.copy(pose.target);
  controls.update();
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

// Swings round the target rather than cutting straight across, which would fly
// the camera through the model on the way back from the far side.
export function animateCameraToPose(
  camera: RigCamera,
  controls: OrbitControls,
  pose: CameraPose,
  token: SnapToken,
  onComplete: () => void,
): void {
  const fromTarget = controls.target.clone();
  const fromOffset = new THREE.Vector3().subVectors(camera.position, fromTarget);
  const toOffset = new THREE.Vector3().subVectors(pose.position, pose.target);
  const fromDistance = fromOffset.length();
  const toDistance = toOffset.length();
  const turn = new THREE.Quaternion().setFromUnitVectors(
    fromOffset.clone().normalize(),
    toOffset.clone().normalize(),
  );
  const step = new THREE.Quaternion();
  const clock = new THREE.Clock();
  let elapsed = 0;

  const tick = () => {
    if (token.cancelled) return;
    elapsed += clock.getDelta();
    const t = Math.min(elapsed / HOME_SECONDS, 1);
    const eased = easeInOutCubic(t);
    step.identity().slerp(turn, eased);
    controls.target.lerpVectors(fromTarget, pose.target, eased);
    camera.position
      .copy(fromOffset)
      .normalize()
      .applyQuaternion(step)
      .multiplyScalar(THREE.MathUtils.lerp(fromDistance, toDistance, eased))
      .add(controls.target);
    controls.update();
    if (t < 1) requestAnimationFrame(tick);
    else onComplete();
  };
  clock.start();
  tick();
}

export function animateCameraToQuaternion(
  camera: RigCamera,
  controls: OrbitControls,
  targetQuaternion: THREE.Quaternion,
  token: SnapToken,
  onComplete: () => void,
): void {
  const target = controls.target.clone();
  const radius = camera.position.distanceTo(target);

  const startDir = new THREE.Vector3().subVectors(camera.position, target).normalize();
  const startQ = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(startDir, new THREE.Vector3(), camera.up),
  );

  const endDir = new THREE.Vector3(0, 0, 1).applyQuaternion(targetQuaternion);

  // Tilt towards -Y for TOP / +Y for BOTTOM so world +Y is always at the top of
  // the screen in those views.
  const upDot = endDir.dot(camera.up);
  if (Math.abs(upDot) > 1 - POLE_TILT_RADIANS) {
    endDir.y -= POLE_TILT_RADIANS * Math.sign(upDot);
    endDir.normalize();
  }

  const endQ = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(endDir, new THREE.Vector3(), camera.up),
  );

  const clock = new THREE.Clock();
  const q = startQ.clone();

  const tick = () => {
    if (token.cancelled) return;
    const step = clock.getDelta() * TURN_RATE;
    q.rotateTowards(endQ, step);
    const dir = new THREE.Vector3(0, 0, 1).applyQuaternion(q);
    camera.position.copy(target).addScaledVector(dir, radius);
    camera.quaternion.rotateTowards(endQ, step);
    camera.updateProjectionMatrix();
    controls.update();
    if (q.angleTo(endQ) > SNAP_SETTLED_RADIANS) requestAnimationFrame(tick);
    else onComplete();
  };
  clock.start();
  tick();
}
